import React, { useContext, useEffect, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import api from "../../services/api";
import QueueSelect from "../QueueSelect";

import { Grid, Paper, Tab, Tabs, Switch, FormControlLabel } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import AvatarUploader from "../AvatarUpload";
import { Can } from "../Can";
import TabPanel from "../TabPanel";

const backendUrl = getBackendUrl();
const path = require("path");

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
  },
  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  updateInput: {
    display: "none",
  },
  updateLabel: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textTransform: "uppercase",
    textAlign: "center",
    cursor: "pointer",
    border: "2px solid #ccc",
    borderRadius: "5px",
    minWidth: 160,
    fontWeight: "bold",
  color: theme.palette.text.primary,
  backgroundColor: theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : undefined,
  border: theme.mode === 'dark' ? `1px solid ${theme.palette.divider}` : '2px solid #ccc',
  },
  errorUpdate: {
    border: "2px solid red",
  },
  errorText: {
    color: "red",
    fontSize: "0.8rem",
    fontWeight: "bold",
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
    borderRadius: 8,
    border: theme.mode === 'dark' ? `1px solid ${theme.palette.divider}` : '1px solid #e0e0e0',
    backgroundColor: theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fafafa',
    transition: 'background-color 0.25s ease, border-color 0.25s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : '#f2f2f2'
    }
  },
  switchLabel: {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
  whatsappNumber: Yup.string()
    .test('whatsapp-validation', 'Número inválido', function(value) {
      if (!value || value === '') return true; // Permite vazio
      return /^\d{10,11}$/.test(value); // Valida apenas números sem o prefixo 55
    })
    .nullable(),
});

const formatWhatsappToDisplay = (number) => {
  if (!number) return "";
  let n = number.replace(/^55/, "");
  if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2-$3");
  if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, "$1 $2-$3");
  return n;
};
const formatWhatsappToSave = (number) => {
  if (!number) return null; // Corrigido: retorna null ao invés de string vazia
  let n = number.replace(/\D/g, "");
  if (n.length === 10 || n.length === 11) return "55" + n;
  return n;
};

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    email: "",
    password: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "disable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "disabled",
    allUserChat: "disabled",
    userClosePendingTicket: "enabled",
    showDashboard: "disabled",
    allowRealTime: "disabled",
    allowConnections: "disabled",
    whatsappNumber: "",
  allowDevice: false,
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(null);
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const [whatsappInput, setWhatsappInput] = useState("");

  const startWorkRef = useRef();
  const endWorkRef = useRef();

  // Função para formatar o valor para exibição
  function formatWhatsappDisplay(value) {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0,2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0,2)}) ${numbers.slice(2,7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0,2)}) ${numbers.slice(2,7)}-${numbers.slice(7,11)}`;
  }

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        // Limpa os campos quando é um novo usuário
        setUser(initialState);
        setWhatsappInput("");
        setSelectedQueueIds([]);
        setWhatsappId(false);
        setProfileUrl(null);
        setAvatar(null);
        return;
      }
      try {
        const { data } = await api.get(`/users/${userId}`);
        //console.log("🔍 Dados completos do usuário:", data);
        //console.log("📱 WhatsApp Number do backend:", data.whatsappNumber, "tipo:", typeof data.whatsappNumber);
        
        // Melhor tratamento do número do WhatsApp
        let whatsappNumberWithoutPrefix = "";
        if (data.whatsappNumber && data.whatsappNumber !== "") {
          whatsappNumberWithoutPrefix = data.whatsappNumber.replace(/^55/, "");
        }
        
        //console.log("📱 WhatsApp Number processado:", whatsappNumberWithoutPrefix);
        
        setUser((prevState) => {
          return { ...prevState, ...data, whatsappNumber: whatsappNumberWithoutPrefix };
        });
        setWhatsappInput(whatsappNumberWithoutPrefix);

        const { profileImage } = data;
        setProfileUrl(
          `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
        );

        const userQueueIds = data.queues?.map((queue) => queue.id);
  setSelectedQueueIds(userQueueIds);
  setWhatsappId(data.whatsappId ?? null);
      } catch (err) {
        console.error("❌ Erro ao buscar usuário:", err);
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  // UseEffect adicional para sincronizar o whatsappInput com o user.whatsappNumber
  useEffect(() => {
    if (user.whatsappNumber !== undefined) {
      setWhatsappInput(user.whatsappNumber || "");
    }
  }, [user.whatsappNumber]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setWhatsappInput("");
  setSelectedQueueIds([]);
  setWhatsappId(null);
    setProfileUrl(null);
    setAvatar(null);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSaveUser = async (values) => {
    const uploadAvatar = async (file) => {
      const formData = new FormData();
      formData.append("userId", file.id);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      const { data } = await api.post(
        `/users/${file.id}/media-upload`,
        formData
      );

      localStorage.setItem("profileImage", data.user.profileImage);
    };

    const normalizedWhatsappId = (whatsappId === "" || whatsappId === false || whatsappId === undefined) 
      ? null 
      : (whatsappId === null ? null : Number(whatsappId));

    const userData = {
      ...values,
      whatsappNumber: !whatsappInput || whatsappInput.trim() === "" ? null : formatWhatsappToSave(whatsappInput),
      whatsappId: Number.isNaN(normalizedWhatsappId) ? null : normalizedWhatsappId,
      queueIds: selectedQueueIds,
    };

    console.log("💾 Dados sendo enviados para o backend:", userData);
    console.log("📱 WhatsApp Number sendo enviado:", userData.whatsappNumber);

    try {
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        console.log("✅ Resposta do backend após atualização:", data);
        if (
          avatar &&
          (!user?.profileImage || !user?.profileImage !== avatar.name)
        )
          // getBasename(avatar)))
          await uploadAvatar(data);
      } else {
        const { data } = await api.post("/users", userData);
        console.log("✅ Resposta do backend após criação:", data);

        if (!user?.profileImage && avatar) await uploadAvatar(user);
      }
      if (userId === loggedInUser.id) {
        handleClose();
        toast.success(i18n.t("userModal.success"));
      } else {
        handleClose();
        toast.success(i18n.t("userModal.success"));
      }
    } catch (err) {
      console.error("❌ Erro ao salvar usuário:", err);
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {userId
            ? `${i18n.t("userModal.title.edit")}`
            : `${i18n.t("userModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <Paper className={classes.mainPaper} elevation={1}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                  className={classes.tab}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  <Tab
                    label={i18n.t("userModal.tabs.permissions")}
                    value={"permissions"}
                  />
                  <Tab
                    label="Configurações"
                    value={"settings"}
                  />
                </Tabs>
              </Paper>
              <Paper className={classes.paper} elevation={0}>
                <DialogContent dividers>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"general"}
                  >
                    <Grid
                      container
                      spacing={1}
                      alignContent="center"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FormControl className={classes.updateDiv}>
                        <AvatarUploader
                          setAvatar={setAvatar}
                          avatar={user.profileImage}
                          companyId={user.companyId}
                        />
                        {user.profileImage && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                              user.profileImage = null;
                              setFieldValue("profileImage", null);
                              setAvatar(null);
                            }}
                          >
                            {i18n.t("userModal.title.removeImage")}
                          </Button>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.password")}
                          type="password"
                          name="password"
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.email")}
                          name="email"
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field name="whatsappNumber">
                          {({ field, form }) => (
                            <TextField
                              label={i18n.t("userModal.form.whatsappNumber")}
                              name="whatsappNumber"
                              type="text"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              value={formatWhatsappDisplay(whatsappInput)}
                              onChange={e => {
                                // Permite apenas números e limita a 11 dígitos
                                const onlyNums = e.target.value.replace(/\D/g, "").slice(0, 11);
                                console.log("✏️ Campo WhatsApp alterado:", { 
                                  valorDigitado: e.target.value, 
                                  apenasNumeros: onlyNums,
                                  whatsappInputAtual: whatsappInput 
                                });
                                setWhatsappInput(onlyNums);
                                // Sincroniza com o Formik
                                form.setFieldValue("whatsappNumber", onlyNums);
                              }}
                              onBlur={() => form.setFieldTouched("whatsappNumber", true)}
                              placeholder="(99) 99999-9999"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">+55</InputAdornment>
                              }}
                              inputProps={{ maxLength: 16 }}
                              error={form.touched.whatsappNumber && Boolean(form.errors.whatsappNumber)}
                              helperText={form.touched.whatsappNumber && form.errors.whatsappNumber}
                            />
                          )}
                        </Field>
                      </Grid>
                    </Grid>

                    {/* Removido: seleção de tema padrão - controle agora no menu do usuário */}

                    {/* Horário de Trabalho */}
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.startWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={startWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="startWork"
                              error={
                                touched.startWork && Boolean(errors.startWork)
                              }
                              helperText={touched.startWork && errors.startWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.endWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={endWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="endWork"
                              error={touched.endWork && Boolean(errors.endWork)}
                              helperText={touched.endWork && errors.endWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                        </Grid>
                        
                      )}
                    />
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"permissions"}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <>
                          {/* Profile Selection */}
                          <Grid container spacing={2} style={{ marginBottom: 16 }}>
                            <Grid item xs={12} md={6}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <InputLabel id="profile-selection-input-label">
                                  {i18n.t("userModal.form.profile")}
                                </InputLabel>
                                <Field
                                  as={Select}
                                  label={i18n.t("userModal.form.profile")}
                                  name="profile"
                                  labelId="profile-selection-label"
                                  id="profile-selection"
                                  required
                                >
                                  <MenuItem value="admin">Admin</MenuItem>
                                  <MenuItem value="user">User</MenuItem>
                                </Field>
                              </FormControl>
                            </Grid>
                          </Grid>
                          
                          {/* Permission Switches */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allTicket")}
                                </span>
                                <Field name="allTicket">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enable"}
                                      onChange={(e) => 
                                        form.setFieldValue("allTicket", e.target.checked ? "enable" : "disable")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allowGroup")}
                                </span>
                                <Field name="allowGroup">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === true}
                                      onChange={(e) => 
                                        form.setFieldValue("allowGroup", e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allHistoric")}
                                </span>
                                <Field name="allHistoric">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("allHistoric", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allUserChat")}
                                </span>
                                <Field name="allUserChat">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("allUserChat", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.userClosePendingTicket")}
                                </span>
                                <Field name="userClosePendingTicket">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("userClosePendingTicket", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allowConnections")}
                                </span>
                                <Field name="allowConnections">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("allowConnections", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.showDashboard")}
                                </span>
                                <Field name="showDashboard">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("showDashboard", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  {i18n.t("userModal.form.allowRealTime")}
                                </span>
                                <Field name="allowRealTime">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={field.value === "enabled"}
                                      onChange={(e) => 
                                        form.setFieldValue("allowRealTime", e.target.checked ? "enabled" : "disabled")
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={6}>
                              <div className={classes.switchContainer}>
                                <span className={classes.switchLabel}>
                                  Permitir Dispositivo
                                </span>
                                <Field name="allowDevice">
                                  {({ field, form }) => (
                                    <Switch
                                      checked={Boolean(field.value)}
                                      onChange={(e) =>
                                        form.setFieldValue("allowDevice", e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  )}
                                </Field>
                              </div>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    />
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"settings"}
                  >
                    {/* 1. Conexão Padrão */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editProfile"
                          yes={() => (
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              fullWidth
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.whatsapp")}
                              </InputLabel>
                              <Field
                                as={Select}
                                value={whatsappId ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "") return setWhatsappId(null);
                                  const parsed = Number(val);
                                  setWhatsappId(Number.isNaN(parsed) ? null : parsed);
                                }}
                                label={i18n.t("userModal.form.whatsapp")}
                              >
                                <MenuItem value={""}>&nbsp;</MenuItem>
                                {whatsApps.map((whatsapp) => (
                                  <MenuItem
                                    key={whatsapp.id}
                                    value={whatsapp.id}
                                  >
                                    {whatsapp.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* 2. Filas */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editQueues"
                          yes={() => (
                            <QueueSelect
                              selectedQueueIds={selectedQueueIds}
                              onChange={(values) => setSelectedQueueIds(values)}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    {/* 3. Mensagem de Despedida */}
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.farewellMessage")}
                      type="farewellMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="farewellMessage"
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        touched.farewellMessage && errors.farewellMessage
                      }
                      variant="outlined"
                      margin="dense"
                    />
                  </TabPanel>
                </DialogContent>
              </Paper>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {userId
                    ? `${i18n.t("userModal.buttons.okEdit")}`
                    : `${i18n.t("userModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;
